<script setup lang="ts">
import { UIIcon } from '@groover-dev/groover-ui'

import TitleOrangeBar from '~/components/ui/Title/OrangeBar.vue'
import VCard from '~/components/ui/VCard.vue'

import { useSeoListGeneration } from '~/composables/useSeoListGeneration'

import { useTagStore } from '~/stores/tag'

import type Tag from '~/entities/tag'
import type TagParent from '~/entities/tagParent'
import type { Influencer } from '~/types/influencer'
import type { SeoListsAsyncData } from '~/types/influencerListTagSeo'

type UrlData = SeoListsAsyncData['urlData']
type ListElement = { title: string; link: string }

type Props = {
  seoPages: Influencer['seo_pages']
}

const props = defineProps<Props>()

const {
  getTagTypeFromExtendedTypes,
  sortSeoListByType,
  generateTopTitleFromUrlData,
  generateLink,
} = useSeoListGeneration()

const { GET_TAG_FROM_ID, GET_PARENT_FROM_ID } = useTagStore()

const urlData = computed<UrlData[]>(() =>
  props.seoPages.map((seoPage) => {
    return seoPage
      .map((page) => ({
        tagTypeName: getTagTypeFromExtendedTypes(page.type),
        tagId: page.id,
        isParent: GET_PARENT_FROM_ID(page.id) !== undefined,
      }))
      .sort((a, b) => {
        return sortSeoListByType(a.tagTypeName, b.tagTypeName)
      })
  }),
)

const list = computed<ListElement[]>(() =>
  props.seoPages.reduce((accumulator, element, index) => {
    const variableEntity = getEntity(element[0].id)
    const fixedEntity = element[1] ? getEntity(element[1].id) : undefined

    if (variableEntity) {
      accumulator.push({
        title: generateTopTitleFromUrlData(urlData.value[index]),
        link: generateLink([
          variableEntity.name,
          ...(fixedEntity ? [fixedEntity.name] : []),
        ]),
      })
    }

    return accumulator
  }, [] as ListElement[]),
)

function getEntity(id: number): TagParent | Tag | undefined {
  return GET_TAG_FROM_ID(id) ?? GET_PARENT_FROM_ID(id)
}
</script>

<template>
  <VCard padding="5">
    <section class="tw-w-full">
      <TitleOrangeBar title-html-tag="h2">
        <template #title>{{
          $t('influencer.profile.about.seoLists.title')
        }}</template>
      </TitleOrangeBar>
      <div
        class="tw-grid tw-grid-cols-1 tw-gap-6 sm:tw-grid-cols-2 md:tw-grid-cols-3"
      >
        <NuxtLinkLocale
          v-for="(element, index) in list"
          :key="index"
          :to="element.link"
          class="tw-flex tw-items-center tw-justify-start"
        >
          <div
            class="tw-mr-4 tw-flex tw-size-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-orange-100 tw-shadow-round"
          >
            <UIIcon name="fa6-solid:star" class="tw-text-orange-500" />
          </div>
          <span class="tw-text-base tw-font-medium hover:tw-underline">{{
            element.title
          }}</span>
        </NuxtLinkLocale>
      </div>
    </section>
  </VCard>
</template>
